import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, TextField, FormControl, InputLabel, Select,
    MenuItem, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    Tabs, Tab, Card, CardContent, Stepper, Step, StepLabel
} from '@mui/material';
import { Add, Edit, Delete, Visibility, Print, Save, CheckCircle } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

const ExamManagement = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [academicYears, setAcademicYears] = useState([]);
    const [classes, setClasses] = useState([]);
    const [sections, setSections] = useState([]);
    const [exams, setExams] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
        academicYearId: '',
        classId: '',
        sectionId: '',
        examId: ''
    });

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState('');
    const [selectedData, setSelectedData] = useState(null);

    const [marks, setMarks] = useState([]);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            const [yearsRes, classesRes, examsRes] = await Promise.all([
                api.get('/academic-years'),
                api.get('/classes'),
                api.get('/exams').catch(() => ({ data: { data: [] } }))
            ]);
            setAcademicYears(yearsRes.data.data || []);
            setClasses(classesRes.data.data || []);
            setExams(examsRes.data.data || getMockExams());

            const current = yearsRes.data.data?.find(y => y.isCurrent) || yearsRes.data.data?.[0];
            if (current) setFilters(prev => ({ ...prev, academicYearId: current.id }));
        } catch (error) {
            console.error('Failed to fetch initial data');
            setExams(getMockExams());
        }
    };

    const getMockExams = () => [
        { id: 1, name: 'Unit Test 1', type: 'unit', startDate: '2025-07-15', endDate: '2025-07-20', maxMarks: 25 },
        { id: 2, name: 'First Semester', type: 'semester', startDate: '2025-10-01', endDate: '2025-10-15', maxMarks: 100 },
        { id: 3, name: 'Unit Test 2', type: 'unit', startDate: '2025-11-15', endDate: '2025-11-20', maxMarks: 25 },
        { id: 4, name: 'Annual Exam', type: 'annual', startDate: '2026-03-01', endDate: '2026-03-15', maxMarks: 100 }
    ];

    const handleClassChange = async (classId) => {
        setFilters({ ...filters, classId, sectionId: '' });
        try {
            const [sectionsRes, subjectsRes] = await Promise.all([
                api.get(`/classes/${classId}/sections`),
                api.get(`/classes/${classId}/subjects`).catch(() => ({ data: { data: [] } }))
            ]);
            setSections(sectionsRes.data.data || []);
            setSubjects(subjectsRes.data.data || getMockSubjects());
        } catch (error) {
            console.error('Failed to fetch data');
            setSubjects(getMockSubjects());
        }
    };

    const getMockSubjects = () => [
        { id: 1, name: 'English', code: 'ENG', maxMarks: 100 },
        { id: 2, name: 'Mathematics', code: 'MATH', maxMarks: 100 },
        { id: 3, name: 'Science', code: 'SCI', maxMarks: 100 },
        { id: 4, name: 'Social Studies', code: 'SST', maxMarks: 100 },
        { id: 5, name: 'Marathi', code: 'MAR', maxMarks: 100 },
        { id: 6, name: 'Hindi', code: 'HIN', maxMarks: 100 }
    ];

    const fetchStudentsForMarks = async () => {
        if (!filters.classId || !filters.examId) {
            toast.warning('Please select class and exam');
            return;
        }
        setLoading(true);
        try {
            const res = await api.get('/students', {
                params: { classId: filters.classId, sectionId: filters.sectionId }
            });
            const studentList = res.data.data || getMockStudents();
            setStudents(studentList);

            // Initialize marks state
            setMarks(studentList.map(s => ({
                studentId: s.id,
                name: s.firstName + ' ' + s.lastName,
                rollNo: s.rollNumber,
                subjects: subjects.map(sub => ({ subjectId: sub.id, name: sub.name, marks: '', grade: '' }))
            })));
        } catch (error) {
            const mockStudents = getMockStudents();
            setStudents(mockStudents);
            setMarks(mockStudents.map(s => ({
                studentId: s.id,
                name: s.name,
                rollNo: s.rollNo,
                subjects: subjects.map(sub => ({ subjectId: sub.id, name: sub.name, marks: '', grade: '' }))
            })));
        } finally {
            setLoading(false);
        }
    };

    const getMockStudents = () => [
        { id: 1, name: 'Rahul Sharma', rollNo: 1 },
        { id: 2, name: 'Priya Patel', rollNo: 2 },
        { id: 3, name: 'Amit Kumar', rollNo: 3 },
        { id: 4, name: 'Sneha Desai', rollNo: 4 },
        { id: 5, name: 'Vikram Singh', rollNo: 5 }
    ];

    const handleMarksChange = (studentIdx, subjectIdx, value) => {
        const newMarks = [...marks];
        newMarks[studentIdx].subjects[subjectIdx].marks = value;

        // Auto-calculate grade
        const numValue = parseFloat(value);
        let grade = '';
        if (numValue >= 90) grade = 'A+';
        else if (numValue >= 80) grade = 'A';
        else if (numValue >= 70) grade = 'B';
        else if (numValue >= 60) grade = 'C';
        else if (numValue >= 50) grade = 'D';
        else if (numValue >= 35) grade = 'E';
        else if (numValue > 0) grade = 'F';

        newMarks[studentIdx].subjects[subjectIdx].grade = grade;
        setMarks(newMarks);
    };

    const handleSaveMarks = async () => {
        setLoading(true);
        try {
            await api.post('/exams/marks', {
                examId: filters.examId,
                classId: filters.classId,
                marks: marks
            });
            toast.success('Marks saved successfully');
        } catch (error) {
            toast.error('Failed to save marks');
        } finally {
            setLoading(false);
        }
    };

    const renderExamsTab = () => (
        <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Exam Schedule</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => { setDialogType('exam'); setOpenDialog(true); }}>
                    Add Exam
                </Button>
            </Box>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Exam Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Start Date</TableCell>
                            <TableCell>End Date</TableCell>
                            <TableCell>Max Marks</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {exams.map(exam => (
                            <TableRow key={exam.id}>
                                <TableCell>{exam.name}</TableCell>
                                <TableCell><Chip label={exam.type} size="small" /></TableCell>
                                <TableCell>{exam.startDate}</TableCell>
                                <TableCell>{exam.endDate}</TableCell>
                                <TableCell>{exam.maxMarks}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={new Date(exam.endDate) < new Date() ? 'Completed' : 'Upcoming'}
                                        size="small"
                                        color={new Date(exam.endDate) < new Date() ? 'success' : 'primary'}
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton size="small"><Edit fontSize="small" /></IconButton>
                                    <IconButton size="small" color="error"><Delete fontSize="small" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );

    const renderMarksEntryTab = () => (
        <>
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Class</InputLabel>
                            <Select
                                value={filters.classId}
                                label="Class"
                                onChange={e => handleClassChange(e.target.value)}
                            >
                                {classes.map(c => (
                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Section</InputLabel>
                            <Select
                                value={filters.sectionId}
                                label="Section"
                                onChange={e => setFilters({ ...filters, sectionId: e.target.value })}
                            >
                                <MenuItem value="">All</MenuItem>
                                {sections.map(s => (
                                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Exam</InputLabel>
                            <Select
                                value={filters.examId}
                                label="Exam"
                                onChange={e => setFilters({ ...filters, examId: e.target.value })}
                            >
                                {exams.map(e => (
                                    <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button variant="contained" onClick={fetchStudentsForMarks} disabled={loading}>
                            Load Students
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {marks.length > 0 && (
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">Enter Marks</Typography>
                        <Button variant="contained" color="success" startIcon={<Save />} onClick={handleSaveMarks}>
                            Save Marks
                        </Button>
                    </Box>
                    <TableContainer sx={{ maxHeight: 500 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Roll No</TableCell>
                                    <TableCell>Student Name</TableCell>
                                    {subjects.slice(0, 6).map(sub => (
                                        <TableCell key={sub.id} align="center">{sub.name}</TableCell>
                                    ))}
                                    <TableCell align="center">Total</TableCell>
                                    <TableCell align="center">%</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {marks.map((student, sIdx) => {
                                    const total = student.subjects.reduce((sum, s) => sum + (parseFloat(s.marks) || 0), 0);
                                    const percentage = (total / (subjects.length * 100) * 100).toFixed(1);
                                    return (
                                        <TableRow key={student.studentId}>
                                            <TableCell>{student.rollNo}</TableCell>
                                            <TableCell>{student.name}</TableCell>
                                            {student.subjects.slice(0, 6).map((sub, subIdx) => (
                                                <TableCell key={sub.subjectId} align="center">
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={sub.marks}
                                                        onChange={e => handleMarksChange(sIdx, subIdx, e.target.value)}
                                                        sx={{ width: 60 }}
                                                        inputProps={{ min: 0, max: 100 }}
                                                    />
                                                </TableCell>
                                            ))}
                                            <TableCell align="center"><b>{total}</b></TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={`${percentage}%`}
                                                    size="small"
                                                    color={parseFloat(percentage) >= 60 ? 'success' : parseFloat(percentage) >= 35 ? 'warning' : 'error'}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </>
    );

    const renderResultsTab = () => (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>View Results</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Class</InputLabel>
                        <Select value={filters.classId} label="Class" onChange={e => handleClassChange(e.target.value)}>
                            {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Exam</InputLabel>
                        <Select value={filters.examId} label="Exam" onChange={e => setFilters({ ...filters, examId: e.target.value })}>
                            {exams.map(e => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <Button variant="contained">View Results</Button>
                </Grid>
            </Grid>
            <Typography color="text.secondary">Select class and exam to view results</Typography>
        </Paper>
    );

    const renderMarksheetTab = () => (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Print Marksheet</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Class</InputLabel>
                        <Select value={filters.classId} label="Class" onChange={e => handleClassChange(e.target.value)}>
                            {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Exam</InputLabel>
                        <Select value={filters.examId} label="Exam" onChange={e => setFilters({ ...filters, examId: e.target.value })}>
                            {exams.map(e => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <Button variant="contained" startIcon={<Print />}>Print</Button>
                </Grid>
            </Grid>
        </Paper>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Exam Management</Typography>

            <Paper sx={{ mb: 2 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable">
                    <Tab label="Exams" />
                    <Tab label="Marks Entry" />
                    <Tab label="Results" />
                    <Tab label="Marksheet" />
                    <Tab label="Grade Settings" />
                </Tabs>
            </Paper>

            {activeTab === 0 && renderExamsTab()}
            {activeTab === 1 && renderMarksEntryTab()}
            {activeTab === 2 && renderResultsTab()}
            {activeTab === 3 && renderMarksheetTab()}
            {activeTab === 4 && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6">Grade Configuration</Typography>
                    <Typography color="text.secondary">Configure grading patterns and policies.</Typography>
                </Paper>
            )}
        </Box>
    );
};

export default ExamManagement;
